import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  listAdmissionRequests, approveRequest, declineRequest,
  listAdmissionWaitlist, offerWaitlistPlace, promoteWaitlistPlace, removeWaitlistPlace,
  listConfirmedAdmissions, cancelConfirmedAdmission,
  type AdmissionRequestRow, type AdmissionSettings, type ConfirmedAdmission, type WaitlistRow,
  SOURCE_LABEL,
} from '../../lib/admissionsAdmin';
import { ApiError } from '../../lib/apiClient';
import { formatDateShort } from '../../lib/format';

type Tab = 'requests' | 'waitlist' | 'confirmed';

/**
 * The operator's queues.
 *
 * ── TABS, NOT SEPARATE SCREENS ───────────────────────────────────────────────────────
 *
 * §55. Requests, the waitlist and confirmed admissions are three views of one event's
 * audience, and an operator moves between them constantly. Three routes would mean three
 * page loads to answer "did I already approve this person".
 *
 * Confirmed admissions LINK to the existing order records rather than replacing the Orders
 * admin (§58): the same order, reachable from either side.
 */
export function AdmissionQueues({
  eventId, settings, onChanged,
}: { eventId: string; settings: AdmissionSettings; onChanged: () => void }) {
  const [tab, setTab] = useState<Tab>(settings.mode === 'request' ? 'requests' : 'confirmed');
  const [error, setError] = useState<string | null>(null);

  const tabs: { id: Tab; label: string }[] = [
    ...(settings.mode === 'request' ? [{ id: 'requests' as Tab, label: 'Requests' }] : []),
    ...(settings.waitlistEnabled ? [{ id: 'waitlist' as Tab, label: 'Waitlist' }] : []),
    { id: 'confirmed', label: 'Confirmed' },
  ];

  return (
    <div className="adm-queues">
      <div className="adm-queues__tabs" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`adm-queues__tab${tab === t.id ? ' is-active' : ''}`}
            onClick={() => { setTab(t.id); setError(null); }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <p className="alert alert--error" role="alert">{error}</p>}

      {tab === 'requests' && (
        <RequestQueue eventId={eventId} onError={setError} onChanged={onChanged} />
      )}
      {tab === 'waitlist' && (
        <WaitlistQueue eventId={eventId} onError={setError} onChanged={onChanged} />
      )}
      {tab === 'confirmed' && (
        <ConfirmedQueue eventId={eventId} onError={setError} onChanged={onChanged} />
      )}
    </div>
  );
}

// ── Requests (§56) ───────────────────────────────────────────────────────────

function RequestQueue({
  eventId, onError, onChanged,
}: { eventId: string; onError: (m: string | null) => void; onChanged: () => void }) {
  const [rows, setRows] = useState<AdmissionRequestRow[] | null>(null);
  const [status, setStatus] = useState('pending');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setRows(null);
    try {
      setRows(await listAdmissionRequests(eventId, status || null));
    } catch (err) {
      onError(describe(err));
    }
  }, [eventId, status, onError]);

  useEffect(() => { void load(); }, [load]);

  async function approve(row: AdmissionRequestRow) {
    if (!window.confirm(
      `Approve ${row.firstName} ${row.lastName} for ${row.partySize} `
      + `${row.partySize === 1 ? 'place' : 'places'}? Tickets are issued and emailed `
      + 'immediately, and the places are taken.',
    )) return;
    setBusyId(row.id);
    onError(null);
    try {
      await approveRequest(row.id);
      await load();
      onChanged();
    } catch (err) { onError(describe(err)); } finally { setBusyId(null); }
  }

  async function decline(row: AdmissionRequestRow) {
    const message = window.prompt(
      'Optional message for the requester. Leave blank to send the standard wording.\n\n'
      + 'This IS shown to them, so it is not the place for internal notes.', '');
    if (message === null) return;
    setBusyId(row.id);
    onError(null);
    try {
      await declineRequest(row.id, { message: message.trim() || null });
      await load();
      onChanged();
    } catch (err) { onError(describe(err)); } finally { setBusyId(null); }
  }

  return (
    <>
      <div className="adm-filters">
        <select className="adm-select" value={status} aria-label="Filter requests by status"
          onChange={(e) => setStatus(e.target.value)}>
          <option value="pending">Awaiting review</option>
          <option value="approved">Approved</option>
          <option value="declined">Declined</option>
          <option value="">All</option>
        </select>
      </div>

      {!rows && <p className="adm-muted">Loading requests…</p>}
      {rows && rows.length === 0 && <p className="adm-lede">No requests here.</p>}

      {rows && rows.length > 0 && (
        <table className="adm-table">
          <thead>
            <tr>
              <th>Name</th><th>Email</th><th>Party</th><th>Submitted</th>
              <th>Status</th><th>Source</th><th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{r.firstName} {r.lastName}</td>
                <td>{r.email}</td>
                <td>{r.partySize}</td>
                <td className="adm-nowrap">{formatDateShort(r.submittedAt)}</td>
                <td><RequestStatus row={r} /></td>
                <td>{r.source === 'admin_entry' ? 'Entered by staff' : 'Public form'}</td>
                <td className="adm-actions">
                  {r.status === 'pending' ? (
                    <>
                      <button type="button" className="button button--primary adm-btn-sm"
                        disabled={busyId === r.id} onClick={() => { void approve(r); }}>
                        Approve
                      </button>
                      <button type="button" className="button button--ghost adm-btn-sm"
                        disabled={busyId === r.id} onClick={() => { void decline(r); }}>
                        Decline
                      </button>
                    </>
                  ) : r.orderId ? (
                    <Link className="button button--ghost adm-btn-sm"
                      to={`/orders/${r.orderId}/tickets`}>Tickets</Link>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {/* §56/§72: internal notes are fetched for operators but never rendered beside an
          attendee's name, where a screen share would expose them. */}
      <p className="adm-hint">
        Approving issues real tickets straight away and takes the places. Declining sends the
        requester your message, never an internal note.
      </p>
    </>
  );
}

function RequestStatus({ row }: { row: AdmissionRequestRow }) {
  if (row.status === 'pending') return <span className="badge badge--pending">Awaiting review</span>;
  if (row.status === 'approved') return <span className="badge badge--ok">Approved</span>;
  if (row.status === 'declined') return <span className="badge badge--error">Declined</span>;
  return <span className="badge badge--muted">{row.status}</span>;
}

// ── Waitlist (§57) ───────────────────────────────────────────────────────────

function WaitlistQueue({
  eventId, onError, onChanged,
}: { eventId: string; onError: (m: string | null) => void; onChanged: () => void }) {
  const [rows, setRows] = useState<WaitlistRow[] | null>(null);
  const [status, setStatus] = useState('waiting');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setRows(null);
    try {
      setRows(await listAdmissionWaitlist(eventId, status || null));
    } catch (err) { onError(describe(err)); }
  }, [eventId, status, onError]);

  useEffect(() => { void load(); }, [load]);

  async function act(row: WaitlistRow, what: 'offer' | 'promote' | 'remove') {
    const prompts = {
      offer: `Offer a place to ${row.firstName} ${row.lastName}? They get 48 hours to accept, `
        + 'and the place is not held for them in the meantime.',
      promote: `Confirm ${row.firstName} ${row.lastName} for ${row.partySize} `
        + `${row.partySize === 1 ? 'place' : 'places'}? Tickets are issued immediately.`,
      remove: `Remove ${row.firstName} ${row.lastName} from the waitlist? The record is kept.`,
    };
    if (!window.confirm(prompts[what])) return;
    setBusyId(row.id);
    onError(null);
    try {
      if (what === 'offer') await offerWaitlistPlace(row.id);
      else if (what === 'promote') await promoteWaitlistPlace(row.id);
      else await removeWaitlistPlace(row.id);
      await load();
      onChanged();
    } catch (err) { onError(describe(err)); } finally { setBusyId(null); }
  }

  return (
    <>
      <div className="adm-filters">
        <select className="adm-select" value={status} aria-label="Filter waitlist by status"
          onChange={(e) => setStatus(e.target.value)}>
          <option value="waiting">Waiting</option>
          <option value="offered">Offered</option>
          <option value="accepted">Confirmed</option>
          <option value="">All</option>
        </select>
      </div>

      {!rows && <p className="adm-muted">Loading the waitlist…</p>}
      {rows && rows.length === 0 && <p className="adm-lede">Nobody here.</p>}

      {rows && rows.length > 0 && (
        <table className="adm-table">
          <thead>
            <tr>
              {/* §57: the operator sees the order. It is never published. */}
              <th>#</th><th>Name</th><th>Email</th><th>Party</th><th>Joined</th>
              <th>Status</th><th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="mono">{r.sequence}</td>
                <td>{r.firstName} {r.lastName}</td>
                <td>{r.email}</td>
                <td>{r.partySize}</td>
                <td className="adm-nowrap">{formatDateShort(r.joinedAt)}</td>
                <td><WaitlistStatus row={r} /></td>
                <td className="adm-actions">
                  {r.status === 'waiting' && (
                    <>
                      <button type="button" className="button button--primary adm-btn-sm"
                        disabled={busyId === r.id} onClick={() => { void act(r, 'promote'); }}>
                        Confirm
                      </button>
                      <button type="button" className="button button--secondary adm-btn-sm"
                        disabled={busyId === r.id} onClick={() => { void act(r, 'offer'); }}>
                        Offer
                      </button>
                      <button type="button" className="button button--ghost adm-btn-sm"
                        disabled={busyId === r.id} onClick={() => { void act(r, 'remove'); }}>
                        Remove
                      </button>
                    </>
                  )}
                  {r.status === 'offered' && (
                    <>
                      <button type="button" className="button button--primary adm-btn-sm"
                        disabled={busyId === r.id} onClick={() => { void act(r, 'promote'); }}>
                        Confirm now
                      </button>
                      <button type="button" className="button button--ghost adm-btn-sm"
                        disabled={busyId === r.id} onClick={() => { void act(r, 'remove'); }}>
                        Remove
                      </button>
                    </>
                  )}
                  {r.status === 'accepted' && r.orderId && (
                    <Link className="button button--ghost adm-btn-sm"
                      to={`/orders/${r.orderId}/tickets`}>Tickets</Link>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {/* §24/§64: no drag-and-drop, and nothing promotes itself. */}
      <p className="adm-hint">
        Ordered by when people joined. Nobody is promoted automatically, and the order is not
        shown to the people on it — so confirming out of order breaks no promise.
      </p>
    </>
  );
}

function WaitlistStatus({ row }: { row: WaitlistRow }) {
  if (row.status === 'waiting') return <span className="badge badge--pending">Waiting</span>;
  if (row.status === 'offered') {
    return (
      <span className="badge badge--pending" title={row.offerExpiresAt ?? undefined}>
        Offered{row.offerExpiresAt ? ` · until ${formatDateShort(row.offerExpiresAt)}` : ''}
      </span>
    );
  }
  if (row.status === 'accepted') return <span className="badge badge--ok">Confirmed</span>;
  return <span className="badge badge--muted">{row.status}</span>;
}

// ── Confirmed (§58) ──────────────────────────────────────────────────────────

function ConfirmedQueue({
  eventId, onError, onChanged,
}: { eventId: string; onError: (m: string | null) => void; onChanged: () => void }) {
  const [rows, setRows] = useState<ConfirmedAdmission[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setRows(null);
    try {
      setRows(await listConfirmedAdmissions(eventId));
    } catch (err) { onError(describe(err)); }
  }, [eventId, onError]);

  useEffect(() => { void load(); }, [load]);

  async function cancel(row: ConfirmedAdmission) {
    if (!window.confirm(
      `Cancel admission for ${row.name}? Their ${row.partySize} `
      + `${row.partySize === 1 ? 'ticket' : 'tickets'} stop working and the places are freed. `
      + 'Nothing is refunded, because nothing was paid.',
    )) return;
    setBusyId(row.orderId);
    onError(null);
    try {
      await cancelConfirmedAdmission(row.orderId);
      await load();
      onChanged();
    } catch (err) { onError(describe(err)); } finally { setBusyId(null); }
  }

  return (
    <>
      {!rows && <p className="adm-muted">Loading confirmed admissions…</p>}
      {rows && rows.length === 0 && <p className="adm-lede">Nobody admitted yet.</p>}

      {rows && rows.length > 0 && (
        <table className="adm-table">
          <thead>
            <tr>
              <th>Name</th><th>Email</th><th>Party</th><th>Checked in</th>
              <th>How</th><th>Confirmed</th><th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.orderId}>
                <td>{r.name}</td>
                <td>{r.email}</td>
                <td>{r.partySize}</td>
                <td className="mono">{r.checkedIn}/{r.partySize}</td>
                <td>
                  {/* §59: the recorded origin. Never guessed from a $0 total. */}
                  <span className="badge badge--muted">
                    {SOURCE_LABEL[r.source] ?? r.source}
                  </span>
                </td>
                <td className="adm-nowrap">{formatDateShort(r.confirmedAt)}</td>
                <td className="adm-actions">
                  <Link className="button button--ghost adm-btn-sm"
                    to={`/orders/${r.orderId}/tickets`}>Tickets</Link>
                  {r.source !== 'ticket_purchase' && (
                    <button type="button" className="button button--ghost adm-btn-sm"
                      disabled={busyId === r.orderId} onClick={() => { void cancel(r); }}>
                      Cancel
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="adm-hint">
        Everyone admitted, however they got in. A purchase is cancelled by refunding it in
        Orders, which has its own finance treatment — not from here.
      </p>
    </>
  );
}

function describe(err: unknown): string {
  if (err instanceof ApiError) {
    const payload = err.payload as { message?: string; error?: string } | null;
    return payload?.message ?? payload?.error ?? err.message;
  }
  return 'That could not be completed.';
}
